import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { PeriodPicker } from './PeriodPicker';
import { KpiCard } from './KpiCard';
import { KpiChart } from './KpiChart';
import { TopList } from './TopList';
import { presetRange, fmtDate } from './periods';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AnalyticsKpis = () => {
  const [preset, setPreset] = useState('30d');
  const [[start, end, prevStart, prevEnd], setRange] = useState(presetRange('30d'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    const params = { start, end, ...(prevStart ? { prev_start: prevStart, prev_end: prevEnd } : {}) };
    axios.get(`${API}/admin/analytics/kpis`, { params, withCredentials: true })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'Could not load KPIs'))
      .finally(() => setLoading(false));
  }, [start, end, prevStart, prevEnd]);

  const choosePreset = (id) => { setPreset(id); setRange(presetRange(id)); };
  const chooseCustom = (s, e) => { setPreset('custom'); setRange(s <= e ? [s, e] : [e, s]); };

  const cur = data?.current;
  const prev = data?.previous;
  return (
    <section className="mb-8 space-y-4" data-testid="analytics-kpis">
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-bold text-gray-900">KPIs</h2>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-[#1a5c3b]" />}
        </div>
        <PeriodPicker preset={preset} start={start} end={end} onPreset={choosePreset} onCustom={chooseCustom} />
        {data && <p className="text-xs text-gray-500" data-testid="kpi-period-label">{fmtDate(data.start)} – {fmtDate(data.end)} · compared with {fmtDate(prev.start)} – {fmtDate(prev.end)}</p>}
      </div>
      {error && <p className="text-sm text-red-600" data-testid="kpi-error">{error}</p>}
      {cur && (
        <div className={`space-y-4 transition-opacity ${loading ? 'opacity-60' : ''}`}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <KpiCard big label="Visits" cur={cur.visits} prev={prev.visits} testid="kpi-visits" />
            <KpiCard big label="Unique visitors" cur={cur.unique_visitors} prev={prev.unique_visitors} testid="kpi-unique" />
            <KpiCard big label="Total leads" cur={cur.total_leads} prev={prev.total_leads} testid="kpi-total-leads" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {Object.entries(data.lead_labels).map(([k, label]) => (
              <KpiCard key={k} label={label} cur={cur.leads[k]} prev={prev.leads[k]} testid={`kpi-lead-${k}`} />
            ))}
          </div>
          <KpiChart series={data.series} unit={data.unit} />
          <div className="grid md:grid-cols-3 gap-3">
            <TopList title="Traffic sources" testid="kpi-sources" rows={data.traffic_sources.map((s) => ({ label: `${s.source} / ${s.medium}`, count: s.count }))} />
            <TopList title="Keywords" testid="kpi-keywords" rows={data.keywords.map((k) => ({ label: k.keyword, count: k.count }))} />
            <TopList title="Cities (Canada)" testid="kpi-cities" rows={data.cities.map((c) => ({ label: c.city, count: c.count }))} />
          </div>
        </div>
      )}
    </section>
  );
};
